import { useState, useEffect } from "react";
import AdminSidebar from "@/pages/admin/components/AdminSidebar";
import { getSupabase } from "@/lib/supabase";
import { Link } from "react-router-dom";

const ADMIN_BASE = "/admin";

interface Stats {
  totalUsers: number;
  totalHackers: number;
  totalProjects: number;
  totalWallets: number;
  openProjects: number;
  totalTransactions: number;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<Stats>({
    totalUsers: 0,
    totalHackers: 0,
    totalProjects: 0,
    totalWallets: 0,
    openProjects: 0,
    totalTransactions: 0,
  });
  const [loading, setLoading] = useState(true);
  const [testStatus, setTestStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [testMessage, setTestMessage] = useState("");
  const [activeTest, setActiveTest] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const supabase = getSupabase();
      const [usersRes, hackersRes, projectsRes, walletsRes, openProjectsRes, txRes] = await Promise.all([
        supabase.from("profiles").select("*", { count: "exact", head: true }),
        supabase.from("hacker_listings").select("*", { count: "exact", head: true }),
        supabase.from("projects").select("*", { count: "exact", head: true }),
        supabase.from("crypto_wallets").select("*", { count: "exact", head: true }),
        supabase.from("projects").select("*", { count: "exact", head: true }).eq("status", "open"),
        supabase.from("crypto_transactions").select("*", { count: "exact", head: true }),
      ]);

      setStats({
        totalUsers: usersRes.count || 0,
        totalHackers: hackersRes.count || 0,
        totalProjects: projectsRes.count || 0,
        totalWallets: walletsRes.count || 0,
        openProjects: openProjectsRes.count || 0,
        totalTransactions: txRes.count || 0,
      });
    } catch {
      // fallback
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async (fnSlug: string, testData: Record<string, unknown>) => {
    setActiveTest(fnSlug);
    setTestStatus("loading");
    setTestMessage("");

    try {
      const supabase = getSupabase();
      const { data, error } = await supabase.functions.invoke(fnSlug, {
        body: testData,
      });

      if (error) {
        setTestStatus("error");
        setTestMessage(error.message || "Edge function returned an error");
        return;
      }

      if (data?.success) {
        setTestStatus("success");
        setTestMessage("Email sent! Check your inbox.");
      } else {
        setTestStatus("error");
        setTestMessage(data?.message || "Edge function responded but email may not have been sent. Check Supabase secrets.");
      }
    } catch (err) {
      setTestStatus("error");
      setTestMessage(err instanceof Error ? err.message : "Failed to invoke edge function");
    } finally {
      setActiveTest(null);
    }
  };

  const statCards = [
    { label: "Total Users", value: stats.totalUsers, icon: "fas fa-users", color: "from-blue-500/10 to-cyan-500/10", textColor: "text-blue-600" },
    { label: "Hackers", value: stats.totalHackers, icon: "fas fa-user-secret", color: "from-purple-500/10 to-pink-500/10", textColor: "text-purple-600" },
    { label: "Total Projects", value: stats.totalProjects, icon: "fas fa-folder-open", color: "from-amber-500/10 to-orange-500/10", textColor: "text-amber-600" },
    { label: "Open Projects", value: stats.openProjects, icon: "fas fa-clock", color: "from-emerald-500/10 to-teal-500/10", textColor: "text-emerald-600" },
    { label: "Crypto Wallets", value: stats.totalWallets, icon: "fas fa-wallet", color: "from-rose-500/10 to-red-500/10", textColor: "text-rose-600" },
    { label: "Transactions", value: stats.totalTransactions, icon: "fas fa-exchange-alt", color: "from-indigo-500/10 to-violet-500/10", textColor: "text-indigo-600" },
  ];

  return (
    <div className="min-h-screen bg-[#161b22]">
      <AdminSidebar />
      <div className="ml-60">
        {/* Top bar */}
        <header className="bg-[#0d1117] border-b border-gray-700 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
          <div>
            <h1 className="text-lg font-bold text-gray-200">Dashboard</h1>
            <p className="text-xs text-gray-400">Overview of your platform</p>
          </div>
          <div className="text-xs text-gray-400">
            {new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </div>
        </header>

        <main className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="fas fa-spinner fa-spin text-[#5cb85c] text-2xl" />
            </div>
          ) : (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
                {statCards.map((card) => (
                  <div key={card.label} className="bg-[#0d1117] rounded-xl border border-gray-700 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${card.color} flex items-center justify-center`}>
                        <i className={`${card.icon} ${card.textColor} text-sm`} />
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-gray-200">{card.value}</p>
                    <p className="text-xs text-gray-400 mt-1">{card.label}</p>
                  </div>
                ))}
              </div>

              {/* Test Email Section */}
              <div className="mb-8 bg-[#0d1117] rounded-xl border border-gray-700 p-6">
                <h3 className="text-sm font-semibold text-gray-200 mb-4">
                  <i className="fas fa-envelope text-gray-400 mr-2" />
                  Test Email Notifications
                </h3>
                <p className="text-xs text-gray-400 mb-5 max-w-2xl">
                  After setting up RESEND_API_KEY and RESEND_FROM_DOMAIN in Supabase secrets, test each notification type below. Emails are sent to your support/concierge inbox.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Hire Request Test */}
                  <div className="bg-[#161b22] rounded-lg border border-gray-700 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 rounded bg-[#5cb85c]/10 flex items-center justify-center">
                        <i className="fas fa-user-secret text-[#5cb85c] text-xs" />
                      </div>
                      <span className="text-xs font-semibold text-gray-300">Hire Request</span>
                    </div>
                    <p className="text-[11px] text-gray-500 mb-3">Sends a dummy hire request notification to concierge.</p>
                    <button
                      onClick={() => sendTestEmail("hire-request-notify", {
                        client_name: "Test Client",
                        email: "test@example.com",
                        requested_hacker: "ShadowByte",
                        hacker_specialty: "Penetration Testing",
                        hacker_rate: "$150/hr",
                        project_type: "Penetration Testing",
                        budget_range: "$5,000 - $10,000",
                        timeline: "1 week",
                        job_description: "Test hire request — Resend config verified!",
                      })}
                      disabled={testStatus === "loading"}
                      className="w-full bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-xs font-semibold py-2 rounded-lg transition-all whitespace-nowrap"
                    >
                      {testStatus === "loading" && activeTest === "hire-request-notify" ? (
                        <span><i className="fas fa-spinner fa-spin mr-1.5" />Sending...</span>
                      ) : (
                        <span><i className="fas fa-paper-plane mr-1.5" />Test Hire Email</span>
                      )}
                    </button>
                  </div>

                  {/* Contact Form Test */}
                  <div className="bg-[#161b22] rounded-lg border border-gray-700 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 rounded bg-[#5bc0de]/10 flex items-center justify-center">
                        <i className="fas fa-envelope text-[#5bc0de] text-xs" />
                      </div>
                      <span className="text-xs font-semibold text-gray-300">Contact Form</span>
                    </div>
                    <p className="text-[11px] text-gray-500 mb-3">Sends a dummy contact form submission to support.</p>
                    <button
                      onClick={() => sendTestEmail("contact-notify", {
                        name: "Test User",
                        email: "test@example.com",
                        subject: "Test contact form submission",
                        message: "This is a test message from the contact form to verify that Resend email notifications are working correctly.",
                      })}
                      disabled={testStatus === "loading"}
                      className="w-full bg-[#5bc0de] hover:bg-[#46b8da] disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-xs font-semibold py-2 rounded-lg transition-all whitespace-nowrap"
                    >
                      {testStatus === "loading" && activeTest === "contact-notify" ? (
                        <span><i className="fas fa-spinner fa-spin mr-1.5" />Sending...</span>
                      ) : (
                        <span><i className="fas fa-paper-plane mr-1.5" />Test Contact Email</span>
                      )}
                    </button>
                  </div>

                  {/* Project Submission Test */}
                  <div className="bg-[#161b22] rounded-lg border border-gray-700 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 rounded bg-amber-500/10 flex items-center justify-center">
                        <i className="fas fa-folder-open text-amber-400 text-xs" />
                      </div>
                      <span className="text-xs font-semibold text-gray-300">Project Submission</span>
                    </div>
                    <p className="text-[11px] text-gray-500 mb-3">Sends a dummy project confirmation to the "client".</p>
                    <button
                      onClick={() => sendTestEmail("project-submit-notify", {
                        email: "test@example.com",
                        project_title: "Test Project — Resend Verification",
                        project_category: "Penetration Testing",
                        project_id: 0,
                        user_id: "00000000-0000-0000-0000-000000000000",
                      })}
                      disabled={testStatus === "loading"}
                      className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-xs font-semibold py-2 rounded-lg transition-all whitespace-nowrap"
                    >
                      {testStatus === "loading" && activeTest === "project-submit-notify" ? (
                        <span><i className="fas fa-spinner fa-spin mr-1.5" />Sending...</span>
                      ) : (
                        <span><i className="fas fa-paper-plane mr-1.5" />Test Project Email</span>
                      )}
                    </button>
                  </div>
                </div>

                {/* Status feedback */}
                {testStatus !== "idle" && (
                  <div className={`mt-4 p-3 rounded-lg text-xs ${
                    testStatus === "success"
                      ? "bg-green-900/20 border border-green-700/30 text-green-400"
                      : "bg-red-900/20 border border-red-700/30 text-red-400"
                  }`}>
                    <i className={`fas ${testStatus === "success" ? "fa-check-circle" : "fa-exclamation-circle"} mr-2`} />
                    {testMessage}
                  </div>
                )}
              </div>

              {/* Quick Links */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Link to={`${ADMIN_BASE}/users`} className="bg-[#0d1117] rounded-xl border border-gray-700 p-5 hover:shadow-md transition-all group">
                  <i className="fas fa-users text-2xl text-gray-400 group-hover:text-[#5cb85c] transition-colors mb-3 block" />
                  <h3 className="text-sm font-semibold text-gray-200 mb-1">Manage Users</h3>
                  <p className="text-xs text-gray-400">View, edit, and manage all registered users</p>
                </Link>
                <Link to={`${ADMIN_BASE}/hackers`} className="bg-[#0d1117] rounded-xl border border-gray-700 p-5 hover:shadow-md transition-all group">
                  <i className="fas fa-user-secret text-2xl text-gray-400 group-hover:text-[#5cb85c] transition-colors mb-3 block" />
                  <h3 className="text-sm font-semibold text-gray-200 mb-1">Manage Hackers</h3>
                  <p className="text-xs text-gray-400">Edit hacker profiles, rankings, and verification</p>
                </Link>
                <Link to={`${ADMIN_BASE}/wallets`} className="bg-[#0d1117] rounded-xl border border-gray-700 p-5 hover:shadow-md transition-all group">
                  <i className="fas fa-wallet text-2xl text-gray-400 group-hover:text-[#5cb85c] transition-colors mb-3 block" />
                  <h3 className="text-sm font-semibold text-gray-200 mb-1">Crypto Wallets</h3>
                  <p className="text-xs text-gray-400">Add and manage cryptocurrency payment addresses</p>
                </Link>
                <Link to={`${ADMIN_BASE}/transactions`} className="bg-[#0d1117] rounded-xl border border-gray-700 p-5 hover:shadow-md transition-all group">
                  <i className="fas fa-exchange-alt text-2xl text-gray-400 group-hover:text-[#5cb85c] transition-colors mb-3 block" />
                  <h3 className="text-sm font-semibold text-gray-200 mb-1">Transaction Log</h3>
                  <p className="text-xs text-gray-400">Track crypto payments from users in real-time</p>
                </Link>
                <Link to={`${ADMIN_BASE}/projects`} className="bg-[#0d1117] rounded-xl border border-gray-700 p-5 hover:shadow-md transition-all group">
                  <i className="fas fa-folder-open text-2xl text-gray-400 group-hover:text-[#5cb85c] transition-colors mb-3 block" />
                  <h3 className="text-sm font-semibold text-gray-200 mb-1">Manage Projects</h3>
                  <p className="text-xs text-gray-400">Review and update project statuses</p>
                </Link>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}